import React, { useCallback } from 'react'
import { components } from 'react-select'
import { useTranslation } from 'react-i18next'
import { Box, Checkbox, FormControlLabel, useTheme } from '@mui/material'

import { EyeIcon } from '../../Icons'
import TextField from '../../TextField/TextField'
import { OptionMulti, OptionSingle } from './Option'


export const MultiMenuList = ({ children, ...props }) => {
  const {t} = useTranslation()
  const theme = useTheme()
  return (
    <components.MenuList {...props}>
      {children}
      <Box
        ml={1}
        mt={1}
        color={'grey.600'}
        fontSize={12}
        fontWeight={500}
        onClick={() => props.setValue([])}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            color: theme.palette.grey[800],
          },
        }}
      >
        {t('actions.deselect_all')}
      </Box>
    </components.MenuList>
  );
}

const MenuList = ({ selectProps, children, ...props }) => {
  const {
    inputRef,
    onMenuClose,
    value,
    onChange,
    isMulti,
    menulistprops = {},
    mainField,
    keyField,
    search = '',
    setSearch,
    showSelected,
    setShowSelected,
    showSelectedValue = true,
  } = selectProps

  const {
    isSearchable = false,
    searchPlaceholder,
    isSelectAll = isMulti,
    selectAllLabel = 'Cocher toutes les cases',
    isAllSelected,
    loadAllOptions,
    isHideable = false,
    hideLabel = 'Cacher les cases blanches',
  } = menulistprops

  const theme = useTheme()

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([])
    } else {
      loadAllOptions()
    }
  }

  const selectAllOption = (
    <>
      <FormControlLabel
        onClick={handleSelectAll}
        control={
          <Checkbox
            checked={isAllSelected}
            indeterminate={!isAllSelected && value?.length > 0}
            sx={{
              '& > input': {
                height: '200%',
                top: '-50%',
              },
            }}
          />
        }
        label={
          <label
            style={{
              display: 'flex',
              fontWeight: 500,
              fontSize: 12,
              lineHeight: 2,
              color: theme.palette.grey[600],
              cursor: 'pointer',
              paddingLeft: 6,
            }}
          >
            {selectAllLabel}
          </label>
        }
      />
    </>
  )

  const showSelectedOption = (
    <FormControlLabel
      onClick={() => setShowSelected(!showSelected)}
      control={<EyeIcon size={1} sx={{ fontSize: 14, mr: 0.5 }} />}
      label={
        <label
          style={{
            display: 'flex',
            fontWeight: 500,
            fontSize: 12,
            lineHeight: 2,
            textDecoration: showSelected ? 'line-through' : 'none',
            cursor: 'pointer',
          }}
        >
          {hideLabel}
        </label>
      }
      sx={{ color: theme.palette.grey[600] }}
    />
  )

  const searchBar = (
    <TextField
      value={search}
      setvalue={setSearch}
      type="search"
      input={{
        size: 'small',
        variant: 'filled',
        placeholder: searchPlaceholder,
        clearable: true,
        endiconchange: null,
        sx: {
          '&:hover': { boxShadow: 'none !important' },
        },
        autoFocus: true,
        onBlur: (e) => {
          if (inputRef.current !== e.relatedTarget) {
            onMenuClose()
          }
        },
      }}
      width="100%"
    />
  )

  const selectedValue = useCallback(() => {
    if (value && search.length === 0) {
      if (isMulti) {
        return value.map((selected) => (
          <OptionMulti
            value={selected[keyField]}
            label={selected[mainField]}
            isSelected={true}
            selectProps={selectProps}
            key={selected[keyField]}
            {...props}
          />
        ))
      } else {
        if (Object.keys(value).length > 0) {
          return (
            <OptionSingle
              value={value[keyField]}
              label={value[mainField]}
              isSelected={true}
              selectProps={selectProps}
              key={value[keyField]}
              {...props}
            />
          )
        }
      }
    }
    return null
  }, [value, search])

  return (
    <components.MenuList showSelected={showSelected} {...props}>
      {(isSearchable || isSelectAll || isHideable) && (
        <Box display="flex" flexDirection="column" gap={1} px={1.5} mb={1}>
          {isSearchable && searchBar}
          {(isSelectAll || isHideable) && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              {isSelectAll && selectAllOption}
              {isHideable && showSelectedOption}
            </Box>
          )}
        </Box>
      )}
      {showSelectedValue && selectedValue()}
      {children}
    </components.MenuList>
  )
}

export default MenuList
